/**
 * TypeScript interfaces for the Accounts API microservice (user endpoints).
 *
 * Sources:
 *   accounts/model/user/user.go
 *   accounts/routes/users/users.go
 *   models/usersetting/usersetting.go
 *
 * API services:
 *   src/api/services/user.ts
 *   src/api/services/user-metadata.ts
 */

// ---------------------------------------------------------------------------
// User — accounts/model/user/user.go → User
// ---------------------------------------------------------------------------

export interface User {
  id: string
  accountID: string
  authID: string
  sapAccountID: string
  sapContactID: string
  email: string
  emailVerified: boolean
  firstName: string
  lastName: string
  termsAcceptedAt?: string
  allowEmailNotifications: boolean
  createdBy: string
  createdAt: string
  updatedBy?: string
  updatedAt?: string
}

// ---------------------------------------------------------------------------
// Request bodies — accounts/routes/users/users.go
// ---------------------------------------------------------------------------

/**
 * PATCH /accounts/:accountId/users/:userId
 * Used by patchUpdateUser and patchUserFullName
 */
export interface PatchUserRequest {
  email?: string
  emailVerified?: boolean
  firstName?: string
  lastName?: string
}

/**
 * POST /accounts/:accountId/users/:userId/password
 */
export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

/**
 * Used internally by ChangeEmailHandler (not directly called from Vue)
 */
export interface ChangeEmailRequest {
  currentEmail: string
  newEmail: string
  confirmEmail: string
}

// ---------------------------------------------------------------------------
// UserSetting — models/usersetting/usersetting.go
// ---------------------------------------------------------------------------

export interface MovedToAnotherDeviceMessage {
  connectedDeviceSerial: string
  smartBridgeSerial: string
}

/** Numeric enum matching usersetting.go → update type */
export enum DeviceUpdateType {
  HasNewTSIPMSensor = 0,
  HasNewNonTSIPMSensor = 1
}
export interface UpdatedDevice {
  name: string
  serial: string
  model: string
  update: DeviceUpdateType
}

export interface BlueSkySetting {
  visited?: boolean
  lastViewedVersion?: string
  pressureUnit?: number
  pmUnit?: number
  tempUnit?: number
  viewedSubscriptionExpired?: boolean
  movedToAnotherDeviceMessages?: MovedToAnotherDeviceMessage[]
  devicesUpdated?: UpdatedDevice[]
  showDataGapLine?: boolean
}

/**
 * GET /accounts/:accountId/users/:userId/settings
 * POST /accounts/:accountId/users/:userId/settings
 * PATCH /accounts/:accountId/users/:userId/settings
 */
export interface UserSetting {
  bluesky?: BlueSkySetting
  updatedAt: string
  updatedBy: string
}
